import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import useReactRouter from 'use-react-router';
import { LoadingOverlay } from '../../../components/LoadingOverlay/LoadingOverlay';
import SearchBar from '../../../components/SearchBar';
import { User } from '../../../graphql/auth';
import { OPERATOR, WhereConstraints } from '../../../graphql/common.datatypes';
import { WcvLogLevel } from '../../../graphql/logs.datatypes';
import getLogs from '../getLogs.api';
import LogDetailsModal from '../LogDetailsModal/LogDetailsModal';
import { SearchFilter } from './SearchFilter';
import Results from './Results';
import { Page } from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import getGroupLogs from '../getGroupLogs.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    results: {
        marginTop: theme.spacing(3)
    }
}));

export interface FilterValues {
    tag: string,
    tags: string[],
    logStatus: string,
    startDate: Date,
    endDate: Date,
    levelFatal: boolean,
    levelError: boolean,
    levelWarn: boolean,
    levelInfo: boolean,
    levelDebug: boolean,
    levelNotes: boolean,
    type?: number | number[],
}

export const LogListContent = (props: { user?: User, notes?: boolean, type?: number|number[], type_id?: number, groupLogs?: boolean }) => {
    const {match}: RouteComponentProps<{ code?: string }> = useReactRouter();
    const {code} = match.params;
    const notes = !!props.notes;
    const classes = useStyles();
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [page, setPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState();
    const initialFilters: FilterValues = {
        tag: '',
        tags: code ? [code] : new Array<string>(),
        logStatus: 'ended',
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().endOf('day').toDate(),
        levelFatal: !notes,
        levelError: !notes,
        levelWarn: !notes,
        levelInfo: !notes,
        levelDebug: false,
        levelNotes: notes,
        type: props.type ? props.type : undefined,
    };

    const applyFilters = (newFilters: FilterValues) => {
        setFilters(newFilters);
        setWhere(getConstraintsFromFilters(newFilters));
    };

    const getConstraintsFromFilters = (newFilters: FilterValues) => {
        const whereConstraints: WhereConstraints = {
            AND: [],
        };

        if (props.user) {
            whereConstraints.AND!.push({column: 'user_id', operator: OPERATOR.EQ, value: props.user.id});
        }

        if (props.type && props.type_id) {
            if (Array.isArray(props.type)) {
                whereConstraints.AND!.push({column: 'type', operator: OPERATOR.IN, value: props.type});
            } else {
                whereConstraints.AND!.push({column: 'type', operator: OPERATOR.EQ, value: props.type});
            }

            whereConstraints.AND!.push({column: 'type_id', operator: OPERATOR.EQ, value: props.type_id});
        } else {
            if (newFilters.type) {
                whereConstraints.AND!.push({column: 'type', operator: OPERATOR.EQ, value: newFilters.type});
            }
        }

        if (newFilters.startDate && newFilters.endDate) {
            whereConstraints.AND!.push({column: 'created_at', operator: OPERATOR.BETWEEN, value: [newFilters.startDate, newFilters.endDate]});
        } else {
            if (newFilters.startDate) {
                whereConstraints.AND!.push({column: 'created_at', operator: OPERATOR.GTE, value: newFilters.startDate});
            } else if (newFilters.endDate) {
                whereConstraints.AND!.push({column: 'created_at', operator: OPERATOR.LTE, value: newFilters.endDate});
            }
        }

        if (notes) {
            whereConstraints.AND!.push({column: 'level', operator: OPERATOR.EQ, value: WcvLogLevel.NOTE});
        } else {
            const levelConstraints = [];
            if (newFilters.levelFatal) levelConstraints.push(WcvLogLevel.FATAL);
            if (newFilters.levelError) levelConstraints.push(WcvLogLevel.ERROR);
            if (newFilters.levelWarn) levelConstraints.push(WcvLogLevel.WARN);
            if (newFilters.levelInfo) levelConstraints.push(WcvLogLevel.INFO);
            if (newFilters.levelDebug) levelConstraints.push(WcvLogLevel.DEBUG);
            if (levelConstraints.length > 0) whereConstraints.AND!.push({
                column: 'level',
                operator: OPERATOR.IN,
                value: levelConstraints
            });
        }

        if (newFilters.tags && newFilters.tags.length > 0) {
            whereConstraints.AND!.push({column: 'code', operator: OPERATOR.IN, value: newFilters.tags});
        }

        return whereConstraints;
    };

    const [filters, setFilters] = useState(initialFilters);
    const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
    const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);
    const logsData = props.groupLogs ? getGroupLogs(rowsPerPage, page, where, orderBy) : getLogs(rowsPerPage, page, where, orderBy);

    let logs;
    if (logsData.data && logsData.data.logs) logs = logsData.data.logs;
    if (logsData.data && logsData.data.groupLogs) logs = logsData.data.groupLogs;

    return (
        <React.Fragment>
            <SearchBar filterContent={<SearchFilter filters={filters} initialFilters={initialFilters}
                                                    applyFilters={applyFilters} notes={notes}
                                                    hideType={!!props.type}/>}/>
            <LoadingOverlay loading={logsData.loading}>
                {logs && (
                    <Results
                        notes={notes}
                        className={classes.results}
                        logs={logs.data}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        paginatorInfo={logs.paginatorInfo}
                        setPage={setPage}
                        selectedLog={selectedLog}
                        setSelectedLog={setSelectedLog}
                        hideType={!!props.type}
                    />
                )}
            </LoadingOverlay>
            {selectedLog && (<LogDetailsModal log={selectedLog} setSelectedLog={setSelectedLog}/>)}
        </React.Fragment>
    );
};

export const MyGroupLogList = () => {
    const classes = useStyles();
    const title = 'My Group Logs';
    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="system info" title={title}/>
            <LogListContent groupLogs={true}/>
        </Page>
    );
};

const LogList = () => {
    const classes = useStyles();
    const title = 'Logs';
    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="system info" title={title}/>
            <LogListContent/>
        </Page>
    );
};


export default LogList;
